/*
 * @Author: Json Chen
 * @Date: 2021-06-25 14:07:11
 * @LastEditTime: 2021-06-30 17:21:45
 * @LastEditors: Json Chen
 * @Description: Price Tier 添加和编辑弹窗  priceTierIndex === -1 是添加, priceTierIndex >= 0 是要编辑的项的索引
 * @FilePath: /unifyxp-v2-store-portal/src/pages/Settings/Product/Components/PriceTierModalForm.tsx
 */

import React from 'react';
import { Form, Input, Modal, Checkbox } from 'antd';
import InputMaxLength from '@/src/pages/Settings/Components/InputMaxLength';
import useResetFormOnCloseModal from '@/src/hooks/useResetFormOnCloseModal';
import styles from '../style.module.less';
import { useI18n } from 'src/hooks/useRouterChange';
import { i18nGet } from 'src/tools/tools';

interface PriceTier {
  uuid?: string;
  title: string;
  currencyCode: string;
  remarks: string;
  deletable: boolean;
}

interface PriceTierModalFormProps {
  // 弹窗是否展示
  visible: boolean;
  // 关闭弹窗
  onCancel: () => void;
  // 默认的currency
  priceTierDefaultCurrency: string;
  // 默认的 currency options 列表
  priceTierCurrencyOptions: {
    name: string;
    code: string;
    reportingCurrency?: boolean;
  }[];
  priceTierList: PriceTier[];
  priceTierIndex: number;
  editingTitle?: string;
  groupItemLength?: number;
  onConfirm: (values: any) => void;
}

const groupPriceTiersByTitle = (
  priceTierList: PriceTier[] = [],
  title: string,
): PriceTier[] => {
  return priceTierList.filter((tier) => tier.title === title);
};

const PriceTierModalForm: React.FC<PriceTierModalFormProps> = ({
  visible,
  onConfirm,
  onCancel,
  priceTierDefaultCurrency,
  priceTierCurrencyOptions,
  priceTierList,
  priceTierIndex,
  editingTitle,
  groupItemLength,
}) => {
  const i18n = useI18n();
  const getI18n = (str) => {
    return i18nGet(i18n, str);
  };
  const [form] = Form.useForm();
  const [modalTitle, setModalTitle] = React.useState<string>('');
  const [confirmDisable, setConfirmDisable] = React.useState<boolean>(false);

  const reportingCurrencyCode = React.useMemo(() => {
    const reportingCurrency = priceTierCurrencyOptions.find(
      (currency) => currency.reportingCurrency === true,
    );
    return reportingCurrency
      ? reportingCurrency.code
      : priceTierDefaultCurrency;
  }, [priceTierCurrencyOptions, priceTierDefaultCurrency]);

  // 货币排序
  const sortedCurrencyOptions = React.useMemo(() => {
    return [...priceTierCurrencyOptions].sort((a, b) => {
      if (a.reportingCurrency) return -1;
      if (b.reportingCurrency) return 1;
      return a.code.localeCompare(b.code);
    });
  }, [priceTierCurrencyOptions]);

  useResetFormOnCloseModal({
    form,
    visible,
  });

  const onOk = () => {
    form.validateFields().then((res) => {
      // reportingCurrency始终在选中列表中
      if (!res.applicableCurrency.includes(reportingCurrencyCode)) {
        res.applicableCurrency = [
          ...res.applicableCurrency,
          reportingCurrencyCode,
        ];
      }
      onConfirm(res);
    });
  };

  const onClose = () => {
    setConfirmDisable(false);
    onCancel();
  };

  React.useEffect(() => {
    if (visible) {
      if (priceTierIndex !== -1 && editingTitle) {
        const groupTiers = groupPriceTiersByTitle(priceTierList, editingTitle);

        if (groupTiers.length > 0) {
          const firstTier = groupTiers[0];
          const currencies = groupTiers.map((tier) => tier.currencyCode);

          if (!currencies.includes(reportingCurrencyCode)) {
            currencies.push(reportingCurrencyCode);
          }

          form.setFieldsValue({
            title: firstTier.title,
            remarks: firstTier.remarks,
            applicableCurrency: currencies,
          });
          setModalTitle(getI18n('storeProductSetting.detail.editPriceTier'));
        }
      } else {
        form.setFieldsValue({
          title: `${getI18n('storeProductSetting.detail.priceTier')} ${
            groupItemLength + 1
          }`,
          remarks: '',
          applicableCurrency: [reportingCurrencyCode],
        });
        setModalTitle(getI18n('storeProductSetting.detail.addNewPriceTier'));
      }
    }
  }, [visible, editingTitle, reportingCurrencyCode]);

  // 获取所有已存在的title（除了当前编辑的）
  const existingTitles = React.useMemo(() => {
    if (priceTierIndex === -1) {
      return [...new Set(priceTierList.map((item) => item.title))];
    } else {
      return [
        ...new Set(
          priceTierList
            .filter((item) => item.title !== editingTitle)
            .map((item) => item.title),
        ),
      ];
    }
  }, [priceTierList, priceTierIndex, editingTitle]);

  return (
    <Modal
      forceRender={true}
      title={modalTitle}
      visible={visible}
      okButtonProps={{
        disabled: confirmDisable,
      }}
      okText={getI18n('general.confirm')}
      cancelText={getI18n('general.cancel')}
      maskClosable={false}
      onOk={onOk}
      onCancel={onClose}>
      <Form
        form={form}
        layout='vertical'
        name='priceTierModalForm'
        className={styles.priceTierModalForm}>
        <Form.Item
          name='title'
          className={styles.input}
          labelCol={{
            span: 24,
          }}
          label={
            <div className={styles.inputLabel}>
              <div className={styles.inputLableTitle}>
                {getI18n('storeProductSetting.detail.priceTierTitle')}*
              </div>
              <InputMaxLength maxLength={50} fieldName='title' />
            </div>
          }
          rules={[
            { required: true },
            {
              validator: (_, value) => {
                const hasSame = existingTitles.includes(value);
                setConfirmDisable(hasSame);
                return hasSame
                  ? Promise.reject(
                      new Error(
                        'A price tier already exists with the same title.',
                      ),
                    )
                  : Promise.resolve();
              },
            },
          ]}>
          <Input
            size='large'
            placeholder='Repetitive price tier title'
            maxLength={50}
          />
        </Form.Item>
        <Form.Item
          name='applicableCurrency'
          className={styles.checkbox}
          label={
            <div className={styles.inputLabel}>
              <div className={styles.inputLableTitle}>
                {getI18n(
                  'storeProductSetting.detail.priceTierAvailableCurrency',
                )}
                *
              </div>
            </div>
          }
          rules={[
            { required: true, message: 'Please select at least one currency' },
          ]}>
          <Checkbox.Group className={styles.currencyCheckboxGroup}>
            {sortedCurrencyOptions.map((option) => (
              <div key={option.code} className={styles.currencyCheckboxItem}>
                <Checkbox
                  value={option.code}
                  disabled={
                    option.reportingCurrency === true ||
                    option.code === reportingCurrencyCode
                  }>
                  {option.code}{' '}
                  {option.reportingCurrency ||
                  option.code === reportingCurrencyCode
                    ? `(${getI18n('storeCurrency.detail.reportingCurrency')})`
                    : ''}
                </Checkbox>
              </div>
            ))}
          </Checkbox.Group>
        </Form.Item>

        <Form.Item
          name='remarks'
          className={styles.input}
          label={
            <div className={styles.inputLabel}>
              <div className={styles.inputLableTitle}>
                {getI18n('storeProductSetting.detail.priceTierRemarks')} (
                {getI18n('general.optional')})
              </div>
              <InputMaxLength maxLength={100} fieldName='remarks' />
            </div>
          }>
          <Input.TextArea maxLength={100} size='large' />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default PriceTierModalForm;
